import { formatPrice, calculateDiscountPercentage } from '../../lib/formatters'
import { LABELS } from '../../lib/labels'
import { PriceDisplay } from '../PriceDisplay'

interface ProductPriceProps {
  price: number
  specialPrice?: number | null
  msrp?: number | null
  priceSuffix?: string
}

/**
 * ProductPrice - Centered price display matching CardItem.tsx
 *
 * Three display modes:
 * 1. Discounted: strikethrough regular + percentage badge + final price
 * 2. Regular with MSRP: PRP label + MSRP value + final price
 * 3. Regular: just final price with "cu TVA" suffix
 *
 * Uses PriceDisplay component for split decimal styling on final price.
 */
export function ProductPrice({
  price,
  specialPrice,
  msrp,
  priceSuffix = LABELS.PRICE_SUFFIX,
}: ProductPriceProps) {
  if (price === 0) return null

  const hasDiscount = specialPrice != null && specialPrice > 0 && specialPrice < price
  const finalPrice = hasDiscount ? specialPrice : price

  return (
    <div className="h-[35px] md:h-[40px] xl:h-[50px] flex flex-col justify-end items-center">
      {hasDiscount ? (
        <DiscountedPrice
          regularPrice={price}
          finalPrice={finalPrice}
          priceSuffix={priceSuffix}
        />
      ) : (
        <RegularPrice
          price={price}
          msrp={msrp}
          priceSuffix={priceSuffix}
        />
      )}
    </div>
  )
}

interface DiscountedPriceProps {
  regularPrice: number
  finalPrice: number
  priceSuffix?: string
}

/**
 * Discounted price: strikethrough + percentage badge + PriceDisplay
 */
function DiscountedPrice({
  regularPrice,
  finalPrice,
  priceSuffix,
}: DiscountedPriceProps) {
  const discountPct = calculateDiscountPercentage(regularPrice, finalPrice)

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-row gap-2 items-center">
        <p className="react-css text-xs xl:text-base font-medium line-through font-main-font">
          {formatPrice(regularPrice)}
        </p>
        <p className="h-[20px] pl-2 pr-2 text-white text-xs xl:text-base font-medium text-center bg-custom-red flex items-center justify-center font-main-font">
          {`- ${discountPct.toFixed(0)}%`}
        </p>
      </div>
      <p className="react-css text-sm md:text-base xl:text-[22px] font-bold text-custom-red font-main-font">
        <PriceDisplay price={finalPrice} size="base" />
        <span className="react-css text-[10px] md:text-[11px] xl:text-base font-medium text-black ml-1 md:ml-2 font-main-font">
          {priceSuffix}
        </span>
      </p>
    </div>
  )
}

interface RegularPriceProps {
  price: number
  msrp?: number | null
  priceSuffix?: string
}

/**
 * Regular price: optional MSRP (PRP:) + PriceDisplay + suffix
 */
function RegularPrice({
  price,
  msrp,
  priceSuffix,
}: RegularPriceProps) {
  return (
    <div className="react-css flex flex-col items-center gap-0 xl:gap-1">
      {msrp != null && msrp > 0 && (
        <div className="react-css flex flex-row items-center">
          <span className="react-css text-xs leading-4 xl:leading-4 xl:text-base font-medium font-main-font">
            {LABELS.MSRP_PREFIX}
          </span>
          <span className="react-css text-xs leading-4 xl:leading-4 xl:text-base font-medium font-main-font">
            {' '}{formatPrice(msrp)}
          </span>
        </div>
      )}
      <p className="react-css text-sm md:text-base xl:text-[22px] font-bold text-custom-red font-main-font">
        <PriceDisplay price={price} size="base" />
        <span className="react-css text-[10px] md:text-[11px] xl:text-base font-medium text-black ml-1 md:ml-2 font-main-font">
          {priceSuffix}
        </span>
      </p>
    </div>
  )
}
